import { formatMoney } from '@/renderer/components/billing';
import {
  getAnnualDiscountPercentage,
  getBillingCycleDescriptor,
  getPackageMonthlyEquivalent,
  getPackageTotalPriceForCycle,
} from '@/renderer/services/billingPricing';
import { BillingCycle } from '@/renderer/types/billing';
import { SubscriptionPackageInfo } from '@/renderer/types/subscription';
import React from 'react';

interface OnboardingPackageCardProps {
  billingCycle: BillingCycle;
  isRecommended?: boolean;
  isSelected: boolean;
  onSelect: () => void;
  packageInfo: SubscriptionPackageInfo;
}

const packageAudienceLabel: Record<SubscriptionPackageInfo['id'], string> = {
  enterprise: 'Best for established multi-branch businesses',
  pro_semi_annual: 'Best for scaling businesses',
  professional: 'Best for growing businesses adding locations',
  starter: 'Best for new teams launching their first outlet',
};

export const OnboardingPackageCard: React.FC<OnboardingPackageCardProps> = ({
  billingCycle,
  isRecommended = false,
  isSelected,
  onSelect,
  packageInfo,
}) => {
  const annualDiscountPercentage = getAnnualDiscountPercentage(packageInfo);
  const monthlyEquivalent = getPackageMonthlyEquivalent(
    packageInfo,
    billingCycle,
  );
  const totalPrice = getPackageTotalPriceForCycle(packageInfo, billingCycle);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative w-full rounded-2xl border p-5 text-left transition-all duration-200 ${
        isSelected
          ? 'border-pos-primary bg-blue-50 shadow-lg ring-2 ring-pos-primary/20'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-gray-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">
          {packageAudienceLabel[packageInfo.id]}
        </span>
        {isRecommended && (
          <span className="rounded-full bg-pos-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
            Best value
          </span>
        )}
        {billingCycle === 'annual' && annualDiscountPercentage > 0 && (
          <span className="rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
            Save {annualDiscountPercentage}%
          </span>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            {packageInfo.name}
          </h3>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            {packageInfo.description}
          </p>
        </div>

        <div
          className={`mt-1 h-5 w-5 rounded-full border-2 ${
            isSelected
              ? 'border-pos-primary bg-pos-primary'
              : 'border-gray-300 bg-white'
          }`}
        >
          {isSelected && (
            <div className="flex h-full items-center justify-center">
              <span className="h-2 w-2 rounded-full bg-white" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-5">
        {billingCycle === 'annual' && (
          <p className="text-sm font-medium text-gray-400 line-through">
            {formatMoney(packageInfo.price)}/month
          </p>
        )}
        <div className="flex items-end gap-2">
          <span className="text-3xl font-bold tracking-tight text-gray-900">
            {formatMoney(monthlyEquivalent)}
          </span>
          <span className="pb-1 text-sm font-medium text-gray-500">/month</span>
        </div>
        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-pos-primary">
          {getBillingCycleDescriptor(billingCycle)}
        </p>
        {billingCycle === 'annual' && (
          <p className="mt-2 text-sm text-gray-500">
            {formatMoney(totalPrice)} charged once after your 14-day trial.
          </p>
        )}
      </div>

      <ul className="mt-5 space-y-2">
        {packageInfo.features.slice(0, 4).map((feature) => (
          <li
            key={feature}
            className="flex items-start gap-2 text-sm text-gray-700"
          >
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </button>
  );
};
